import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Loader2, Mail, Phone, MapPin, Calendar, BookOpen, 
  Camera, Upload, FileText, ChevronRight, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentProfile() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchProfile();
  }, [navigate]);

  const fetchProfile = async () => {
    const token = localStorage.getItem('studentToken');
    
    try {
      const response = await fetch(`${API_URL}/api/student-portal/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Allow up to 2MB raw — auto-compressed to ~50-80KB
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    setUploading(true);

    try {
      const { readFileSmart } = await import('@/utils/imageCompressor');
      const result = await readFileSmart(file, { photoMaxDim: 600, quality: 0.85 });
      const base64Data = result.data;

      const token = localStorage.getItem('studentToken');
      const response = await fetch(`${API_URL}/api/student-portal/upload-profile-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ photo: base64Data })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({
          ...prev,
          profile: { ...prev.profile, photo_url: data.photo_url }
        }));
        toast.success('Profile photo updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload photo');
      }
      setUploading(false);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
      setUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const student = profile?.profile || {};
  const photoUrl = student.photo_url || student.profile_photo;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl font-semibold">My Profile</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Header Card with Photo Upload */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Profile Photo with Upload */}
              <div className="relative group">
                <div className="w-28 h-28 rounded-full overflow-hidden bg-indigo-100 flex items-center justify-center border-4 border-white shadow-lg">
                  {photoUrl ? (
                    <img 
                      src={photoUrl} 
                      alt={student.full_name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-14 h-14 text-indigo-600" />
                  )}
                </div>
                {/* Upload Overlay */}
                <div 
                  className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : (
                    <Camera className="w-8 h-8 text-white" />
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                {/* Camera Badge */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute bottom-0 right-0 bg-indigo-600 text-white p-2 rounded-full shadow-lg hover:bg-indigo-700 transition-colors"
                >
                  <Camera className="w-4 h-4" />
                </button>
              </div>
              
              <div className="text-center md:text-left flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{student.full_name}</h2>
                <p className="text-lg text-indigo-600 font-medium">{profile?.student_id}</p>
                <p className="text-gray-500">{profile?.course} • {profile?.session}</p>
                <p className="text-xs text-gray-400 mt-2">Click on photo to update (max 70KB)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-3">
              <Link to="/student/documents">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 hover:bg-indigo-50 hover:border-indigo-300">
                  <FileText className="w-6 h-6 text-indigo-600" />
                  <span className="text-sm">My Documents</span>
                </Button>
              </Link>
              <Link to="/student/change-password">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 hover:bg-indigo-50 hover:border-indigo-300">
                  <ChevronRight className="w-6 h-6 text-indigo-600" />
                  <span className="text-sm">Change Password</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Personal Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{student.email || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{student.phone || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Date of Birth</p>
                  <p className="font-medium">{student.date_of_birth || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Gender</p>
                  <p className="font-medium">{student.gender || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Address</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Full Address</p>
                  <p className="font-medium">
                    {student.address || '-'}<br />
                    {student.city && `${student.city}, `}
                    {student.district && `${student.district}, `}
                    {student.state}<br />
                    {student.pin_code && `PIN: ${student.pin_code}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Academic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Academic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Admission Number</p>
                  <p className="font-medium">{student.admission_number || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Roll Number</p>
                  <p className="font-medium">{student.roll_number || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Admission Date</p>
                  <p className="font-medium">{student.admission_date || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Parents Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Parents Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Father's Name</p>
                <p className="font-medium">{student.father_name || '-'}</p>
                <p className="text-sm text-gray-500">{student.father_phone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Mother's Name</p>
                <p className="font-medium">{student.mother_name || '-'}</p>
                <p className="text-sm text-gray-500">{student.mother_phone}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
